import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Mail, Edit2, Plus, Trash2, Search, Loader2, Save, ChevronDown, ChevronRight, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import EmailSenderCard from './EmailSenderCard';

const CATEGORY_COLORS = {
  user: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  admin: 'bg-red-500/20 text-red-300 border-red-500/30',
  workshop: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  system: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
  custom: 'bg-teal-500/20 text-teal-300 border-teal-500/30',
};

export default function AdminEmailsTab() {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEmail, setEditingEmail] = useState(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState({ name: '', subject: '', body_html: '', category: 'custom' });
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedKey, setExpandedKey] = useState(null);
  const [formInvitations, setFormInvitations] = useState([]);
  const [agreements, setAgreements] = useState([]);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const [catalogRes, invRes, agrRes] = await Promise.all([
        axios.get(`${API}/admin/emails/catalog`, { headers }),
        axios.get(`${API}/practitioner-forms/admin/invitations`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/practitioner-forms/admin/agreements`, { headers }).catch(() => ({ data: [] })),
      ]);
      setCatalog(catalogRes.data);
      setFormInvitations(invRes.data);
      setAgreements(agrRes.data);
    } catch (err) {
      toast.error('Failed to load email catalog');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCatalog(); }, [fetchCatalog]);

  const filtered = catalog.filter(e =>
    !search || e.name.toLowerCase().includes(search.toLowerCase()) ||
    e.trigger.toLowerCase().includes(search.toLowerCase()) ||
    (e.category || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleEdit = (email) => {
    setEditingEmail(email);
    setEditSubject(email.subject_override || email.default_subject || '');
    setEditBody(email.body_override || '');
  };

  const handleSave = async () => {
    if (!editingEmail) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/emails/templates/${editingEmail.key}`, {
        subject: editSubject || null,
        body_html: editBody || null,
      }, { headers });
      toast.success('Template updated');
      setEditingEmail(null);
      fetchCatalog();
    } catch (err) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (email) => {
    try {
      await axios.put(`${API}/admin/emails/templates/${email.key}`, {
        enabled: !email.enabled,
      }, { headers });
      fetchCatalog();
    } catch (err) {
      toast.error('Failed to toggle email');
    }
  };

  const handleCreate = async () => {
    if (!newEmail.name || !newEmail.subject) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/emails/templates`, newEmail, { headers });
      toast.success('Custom email created');
      setShowCreate(false);
      setNewEmail({ name: '', subject: '', body_html: '', category: 'custom' });
      fetchCatalog();
    } catch (err) {
      toast.error('Failed to create email');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key) => {
    if (!window.confirm('Delete this custom email?')) return;
    try {
      await axios.delete(`${API}/admin/emails/templates/${key}`, { headers });
      toast.success('Email deleted');
      fetchCatalog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-teal-400" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-emails-tab">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search emails..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 bg-white/5 border-white/10"
            data-testid="email-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">{catalog.length} emails</span>
          <Button size="sm" onClick={() => setShowCreate(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="create-email-btn">
            <Plus className="w-4 h-4 mr-1" /> New Email
          </Button>
        </div>
      </div>

      {/* Which service actually sends the mail */}
      <EmailSenderCard />

      {/* Practitioner Onboarding Forms Section */}
      <Card className="bg-white/5 border-teal-500/30">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-teal-400" />
              <h3 className="font-semibold text-gray-200">Practitioner Onboarding Forms</h3>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span>{formInvitations.length} invitations sent</span>
              <span className="text-gray-600">|</span>
              <span>{agreements.length} agreements signed</span>
            </div>
          </div>
          <p className="text-xs text-gray-400 mb-3">
            Forms are sent from the Members tab (View &rarr; Send Onboarding Form). Practitioners fill out the agreement and profile at <code className="text-teal-400">/dashboard/practitioner-onboarding</code>.
          </p>

          {/* Recent invitations */}
          {formInvitations.length > 0 && (
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {formInvitations.slice(0, 5).map(inv => (
                <div key={inv.id} className="flex items-center justify-between text-xs p-2 rounded bg-white/5">
                  <span className="text-gray-300">{inv.user_name} ({inv.user_email})</span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500">{new Date(inv.sent_at).toLocaleDateString()}</span>
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${
                        inv.completed
                          ? 'bg-green-500/20 text-green-300 border-green-500/30'
                          : inv.agreement_signed_at
                          ? 'bg-sky-500/20 text-sky-300 border-sky-500/30'
                          : 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                      }`}
                      data-testid={`invitation-stage-${inv.id}`}
                    >
                      {inv.stage || (inv.completed ? 'Onboarding complete' : 'Invitation sent — not started')}
                    </Badge>
                    {inv.email_status === 'not_delivered' && (
                      <Badge
                        variant="outline"
                        className="text-[10px] bg-red-500/20 text-red-300 border-red-500/30"
                        data-testid={`invitation-email-failed-${inv.id}`}
                      >
                        Email not delivered
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Catalog — Expandable Rows */}
      <div className="space-y-2">
        {filtered.map(email => {
          const isExpanded = expandedKey === email.key;
          const subject = email.subject_override || email.default_subject || '(No subject set)';
          const preview = email.default_preview || '';

          return (
            <Card key={email.key} className={`bg-white/5 border-white/10 overflow-hidden transition-colors ${isExpanded ? 'border-teal-500/40' : ''}`}>
              <div
                className="flex items-center gap-3 p-3 cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setExpandedKey(isExpanded ? null : email.key)}
                data-testid={`email-row-${email.key}`}
              >
                {/* Expand arrow */}
                <div className="flex-shrink-0 text-gray-400">
                  {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </div>

                {/* Email Name + Category */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="font-medium text-gray-200 truncate">{email.name}</span>
                    <Badge variant="outline" className={`text-[10px] ${CATEGORY_COLORS[email.category] || CATEGORY_COLORS.system}`}>
                      {email.category}
                    </Badge>
                    {(email.subject_override || email.body_override) && (
                      <Badge variant="outline" className="text-[10px] bg-amber-500/20 text-amber-300 border-amber-500/30">Modified</Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">Trigger: {email.trigger}</p>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                  <Switch checked={email.enabled} onCheckedChange={() => handleToggle(email)} />
                  <Button size="sm" variant="ghost" onClick={() => handleEdit(email)} className="text-gray-400 hover:text-white" title="Edit">
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                  {email.type === 'custom' && (
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(email.key)} className="text-red-400 hover:text-red-300" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-1 border-t border-white/5 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Subject</label>
                    <p className="text-sm text-gray-200 mt-0.5">{subject}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Content Summary</label>
                    <p className="text-sm text-gray-300 mt-0.5 leading-relaxed">{preview || 'No preview available — click Edit to view or set content.'}</p>
                  </div>
                  {email.body_override && (
                    <div>
                      <label className="text-xs font-medium text-amber-400 uppercase tracking-wide">Custom Body Override</label>
                      <div className="mt-1 p-3 bg-white/5 rounded-lg text-sm text-gray-300 max-h-40 overflow-y-auto whitespace-pre-wrap">
                        {email.body_override}
                      </div>
                    </div>
                  )}
                  {email.last_modified && (
                    <p className="text-xs text-gray-500">Last modified: {new Date(email.last_modified).toLocaleString()}</p>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingEmail} onOpenChange={() => setEditingEmail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit: {editingEmail?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {editingEmail?.default_subject && (
              <div className="p-3 bg-white/5 rounded-lg">
                <label className="text-xs text-gray-500">Default Subject</label>
                <p className="text-sm text-gray-300">{editingEmail.default_subject}</p>
              </div>
            )}
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Subject Line Override</label>
              <Input
                value={editSubject}
                onChange={e => setEditSubject(e.target.value)}
                placeholder="Leave blank to use default"
                data-testid="email-subject-input"
              />
            </div>
            {editingEmail?.default_preview && (
              <div className="p-3 bg-white/5 rounded-lg">
                <label className="text-xs text-gray-500">Default Content Summary</label>
                <p className="text-sm text-gray-300">{editingEmail.default_preview}</p>
              </div>
            )}
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Body HTML Override</label>
              <Textarea
                value={editBody}
                onChange={e => setEditBody(e.target.value)}
                placeholder="Leave blank to use default template. Use HTML for formatting."
                rows={8}
                data-testid="email-body-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingEmail(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-email-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Custom Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Email Name</label>
              <Input value={newEmail.name} onChange={e => setNewEmail(p => ({...p, name: e.target.value}))} placeholder="e.g., Welcome Back" data-testid="new-email-name" />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Subject</label>
              <Input value={newEmail.subject} onChange={e => setNewEmail(p => ({...p, subject: e.target.value}))} placeholder="Email subject line" />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Body HTML</label>
              <Textarea value={newEmail.body_html} onChange={e => setNewEmail(p => ({...p, body_html: e.target.value}))} rows={6} placeholder="<p>Hello {{name}},</p>" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving || !newEmail.name || !newEmail.subject} className="bg-teal-600 hover:bg-teal-700" data-testid="create-email-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
